// Gamepad support (docs/09 Phase 7, Steam Deck): standard mapping, polled each frame.
// A pull / confirm · B collect / back · X pay now · Y The Hand · D-pad ←/→ pick a reel, ↑/↓ nudge it ·
// RB hold the picked reel · Start settings · Select odds. In bonus games A/B are the game's two choices.
import { controller, getRenderer } from '../render';
import { getGame, getUI, setUI } from '../state/store';

const BTN = { A: 0, B: 1, X: 2, Y: 3, LB: 4, RB: 5, SELECT: 8, START: 9, UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 } as const;

function press(b: number): void {
  const ui = getUI();
  const run = getGame().run;
  const views = getRenderer()?.bonus;
  if (b === BTN.START) return setUI({ panel: ui.panel === 'settings' ? null : 'settings' });
  if (b === BTN.SELECT) return setUI({ panel: ui.panel === 'odds' ? null : 'odds' });
  if (ui.panel) {
    if (b === BTN.B) setUI({ panel: null });
    return;
  }
  if (!run || ui.screen !== 'machine') return;
  if (ui.stage === 'bonus' && run.bonus) {
    const mode = run.bonus.mode;
    if (mode === 'hilo') {
      if (b === BTN.A || b === BTN.UP) views?.hilo.guess('hi');
      if (b === BTN.B || b === BTN.DOWN) views?.hilo.guess('lo');
      if (b === BTN.X) views?.hilo.collectNow();
    } else if (mode === 'plinko') {
      if (b === BTN.LEFT) views?.plinko.move(-1);
      if (b === BTN.RIGHT) views?.plinko.move(1);
      if (b === BTN.A) views?.plinko.dropNow();
    } else if (mode === 'feast' && b === BTN.A) views?.feast.serveNow();
    else if (mode === 'wheel' && b === BTN.A) views?.wheel.stop();
    return;
  }
  if (run.phase === 'gamble') {
    if (b === BTN.A) controller.act({ t: 'GAMBLE_ACCEPT' });
    if (b === BTN.B) controller.act({ t: 'GAMBLE_DECLINE' });
    return;
  }
  if (ui.stage === 'intermission') {
    if (b === BTN.A) controller.act({ t: 'CONTINUE' });
    return;
  }
  const reels = run.stops.length;
  const reel = Math.min(ui.padReel, reels - 1);
  switch (b) {
    case BTN.A:
      return controller.pull(false);
    case BTN.B:
      if (run.phase === 'nudge') controller.act({ t: 'COLLECT' });
      return;
    case BTN.X:
      return void controller.act({ t: 'PAY_NOW' });
    case BTN.Y:
      return controller.toggleHand();
    case BTN.LEFT:
    case BTN.RIGHT:
      return setUI({ padReel: (reel + (b === BTN.LEFT ? reels - 1 : 1)) % reels });
    case BTN.UP:
    case BTN.DOWN:
      return void controller.act({ t: 'NUDGE', reel, dir: b === BTN.UP ? -1 : 1 });
    case BTN.RB:
      return void controller.act({ t: 'TOGGLE_HOLD', reel });
  }
}

export function installGamepad(): void {
  const held = new Map<string, boolean[]>();
  const poll = () => {
    const pads = navigator.getGamepads?.() ?? [];
    let any = false;
    for (const pad of pads) {
      if (!pad || pad.mapping !== 'standard') continue;
      any = true;
      const prev = held.get(pad.id) ?? [];
      pad.buttons.forEach((btn, i) => {
        if (btn.pressed && !prev[i]) press(i);
        prev[i] = btn.pressed;
      });
      held.set(pad.id, prev);
    }
    if (any !== getUI().padActive) setUI({ padActive: any });
    requestAnimationFrame(poll);
  };
  window.addEventListener('gamepadconnected', () => setUI({ padActive: true }));
  requestAnimationFrame(poll);
}
